// API-Client für die Mehr-Produkt-Abrechnung (/api/stripe/*)

import { supabase } from './supabase';

export type BillingInterval = 'month' | 'year' | 'one_time';
export type ProductStatus = 'pending' | 'active' | 'past_due' | 'canceled' | 'expired';

export interface CustomerProduct {
  id: string;
  name: string;
  amount: number; // Cent
  currency: string;
  interval: BillingInterval;
  status: ProductStatus;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  created_at: string;
}

export interface BillingInvoice {
  id: string;
  number: string | null;
  created: number | null;
  total: number | null;
  currency: string;
  status: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

export interface BillingInfo {
  has_stripe_customer: boolean;
  products: CustomerProduct[];
  invoices: BillingInvoice[];
  publivo?: { manage_url: string } | null;
}

export interface CatalogItem {
  id: string;
  name: string;
  description: string | null;
  default_amount: number | null;
  currency: string;
  interval: BillingInterval;
}

async function authFetch(path: string, options: RequestInit = {}, customerId?: string): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Nicht eingeloggt');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session.access_token}`,
    ...(options.headers as Record<string, string> | undefined),
  };
  if (customerId) headers['X-Customer-Id'] = customerId;
  return fetch(path, { ...options, headers });
}

async function parseJson<T>(res: Response): Promise<T> {
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((json as any).error || `Fehler (HTTP ${res.status})`);
  return json as T;
}

// ── Kunde ────────────────────────────────────────────────────────────────
export async function getBilling(customerId?: string): Promise<BillingInfo> {
  const res = await authFetch('/api/stripe/billing', { method: 'GET' }, customerId);
  return parseJson<BillingInfo>(res);
}

export async function activateProduct(customerProductId: string, customerId?: string): Promise<string> {
  const res = await authFetch('/api/stripe/create-checkout', { method: 'POST', body: JSON.stringify({ customer_product_id: customerProductId }) }, customerId);
  const json = await parseJson<{ checkout_url: string }>(res);
  return json.checkout_url;
}

export async function cancelProduct(customerProductId: string, action: 'cancel' | 'reactivate', customerId?: string) {
  const res = await authFetch('/api/stripe/cancel-subscription', { method: 'POST', body: JSON.stringify({ customer_product_id: customerProductId, action }) }, customerId);
  return parseJson(res);
}

export async function getCheckoutStatus(sessionId: string, customerId?: string): Promise<{ ready: boolean; status: string | null }> {
  const res = await authFetch(`/api/stripe/checkout-status?session_id=${encodeURIComponent(sessionId)}`, { method: 'GET' }, customerId);
  return parseJson(res);
}

export async function openPortal(customerId?: string): Promise<string> {
  const res = await authFetch('/api/stripe/create-portal', { method: 'POST' }, customerId);
  const json = await parseJson<{ portal_url: string }>(res);
  return json.portal_url;
}

// ── Admin ────────────────────────────────────────────────────────────────
export async function getCatalog(): Promise<CatalogItem[]> {
  const res = await authFetch('/api/stripe/admin-billing?resource=catalog', { method: 'GET' });
  const json = await parseJson<{ catalog: CatalogItem[] }>(res);
  return json.catalog;
}

export async function createCatalogItem(item: { name: string; description?: string; default_amount?: number | null; interval: BillingInterval }): Promise<CatalogItem> {
  const res = await authFetch('/api/stripe/admin-billing', { method: 'POST', body: JSON.stringify({ op: 'create-catalog', ...item }) });
  const json = await parseJson<{ catalog_item: CatalogItem }>(res);
  return json.catalog_item;
}

export async function assignProduct(input: { customer_id: string; catalog_id?: string | null; name: string; amount: number; interval: BillingInterval }): Promise<CustomerProduct> {
  const res = await authFetch('/api/stripe/admin-billing', { method: 'POST', body: JSON.stringify({ op: 'assign', ...input }) });
  const json = await parseJson<{ product: CustomerProduct }>(res);
  return json.product;
}

export async function removeProduct(customerProductId: string) {
  const res = await authFetch('/api/stripe/admin-billing', { method: 'POST', body: JSON.stringify({ op: 'remove', customer_product_id: customerProductId }) });
  return parseJson(res);
}

// ── Anzeige-Helfer ─────────────────────────────────────────────────────────
export function formatMoney(amountInCents: number | null | undefined, currency = 'eur'): string {
  if (amountInCents == null) return '—';
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency.toUpperCase() }).format(amountInCents / 100);
  } catch {
    return `${(amountInCents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

export const intervalLabels: Record<BillingInterval, string> = {
  month: '/ Monat',
  year: '/ Jahr',
  one_time: 'einmalig',
};

export const productStatusInfo: Record<ProductStatus, { label: string; cls: string }> = {
  pending: { label: 'Noch nicht aktiviert', cls: 'bg-amber-100 text-amber-800' },
  active: { label: 'Aktiv', cls: 'bg-green-100 text-green-800' },
  past_due: { label: 'Zahlung offen', cls: 'bg-amber-100 text-amber-800' },
  canceled: { label: 'Gekündigt', cls: 'bg-red-100 text-red-800' },
  expired: { label: 'Abgelaufen', cls: 'bg-gray-100 text-gray-600' },
};
